/**
 * AudioTile — audio upload card sized for the session audio slot.
 *
 * Mirrors the prototype's compact-card look while we wait for the
 * full WaveformPlayer batch — once that ships, this component
 * goes away in favour of the wavesurfer-backed `.ws-wrap`.
 *
 *   <AudioTile
 *     label="Session Audio"
 *     required
 *     hint="MP3 · uploaded once · CloudFront-hosted"
 *     value={formData.session_main_url_preview || formData.session_main_url}
 *     uploading={!!formData.session_main_url_uploading}
 *     onChange={(e) => onMainMediaUpload(e)}
 *     onLoadedMetadata={onAudioLoadedData}
 *     onRemove={() => …}
 *   />
 *
 * Behavior:
 *   • Empty: dashed cream tile, click to open file picker
 *   • Populated: filename + native <audio controls> + Replace/Remove
 *   • `onLoadedMetadata` is forwarded straight through so the parent
 *     can still read `e.target.duration` and update session_length.
 */
import React, { useRef } from 'react';
import { cn } from '../cn';

function deriveFilename(url) {
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) return '(pending upload)';
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    const last = String(url).split('/').filter(Boolean).pop();
    return last || String(url);
  }
}

const UploadIconSvg = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

export default function AudioTile({
  label,
  required,
  hint,
  value,
  uploading,
  accept = 'audio/*',
  onChange,
  onLoadedMetadata,
  onRemove,
  className,
  inputId,
  // Optional `name` for the hidden file input (form semantics / tests).
  name,
}) {
  const inputRef = useRef(null);
  const hasValue = !!value;

  const openPicker = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFile = (e) => {
    if (onChange) onChange(e);
    e.target.value = '';
  };

  const filename = deriveFilename(value);

  return (
    <div className={cn('flex flex-col', className)} style={{ minWidth: 0 }}>
      {label && (
        <label
          className="font-condensed font-bold uppercase text-text-light"
          htmlFor={inputId}
          style={{
            fontSize: 11,
            letterSpacing: '0.08em',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <span>{label}</span>
          {required && (
            <span
              className="text-coral"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
              }}
            >
              *
            </span>
          )}
          {hint && (
            <span
              className="text-text-muted"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
              }}
            >
              — {hint}
            </span>
          )}
        </label>
      )}

      <div
        style={{
          background: 'rgb(var(--color-cream))', /* cream */
          border: hasValue ? '1px solid rgb(var(--color-sand))' : '1px dashed #D6CCAE',
          borderRadius: 8,
          padding: 14,
          transition: 'all 200ms ease',
          cursor: hasValue ? 'default' : 'pointer',
          minWidth: 0,
        }}
        onClick={hasValue ? undefined : openPicker}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
          e.currentTarget.style.borderStyle = 'solid';
          e.currentTarget.style.background = 'rgb(var(--color-surface))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = hasValue ? 'rgb(var(--color-sand))' : '#D6CCAE';
          e.currentTarget.style.borderStyle = hasValue ? 'solid' : 'dashed';
          e.currentTarget.style.background = 'rgb(var(--color-cream))';
        }}
      >
        {!hasValue ? (
          <div
            className="flex flex-col items-center"
            style={{
              gap: 6,
              padding: '18px 14px',
              textAlign: 'center',
              color: '#9CA1A8',
            }}
          >
            {UploadIconSvg}
            <span style={{ fontSize: 12, fontWeight: 500 }}>Click to upload audio</span>
            <span style={{ fontSize: 11 }}>MP3 / M4A / WAV</span>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 10, position: 'relative' }}>
            <div
              className="text-navy"
              style={{
                fontSize: 12,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={filename}
            >
              {filename}
            </div>
            <audio
              src={value}
              controls
              onLoadedMetadata={onLoadedMetadata}
              style={{ width: '100%', height: 32 }}
            />
            {uploading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#FAF4EAE0',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  color: '#5C7E72',
                  fontWeight: 600,
                  borderRadius: 6,
                }}
              >
                Uploading…
              </div>
            )}
            <div
              className="flex"
              style={{ gap: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={openPicker}
                className="font-condensed font-bold uppercase cursor-pointer"
                style={{
                  flex: 1,
                  height: 28,
                  padding: '0 10px',
                  border: '1px solid rgb(var(--color-sand))',
                  borderRadius: 4,
                  background: 'rgb(var(--color-surface))',
                  color: 'rgb(var(--color-navy))',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                }}
              >
                Replace
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="font-condensed font-bold uppercase cursor-pointer"
                  style={{
                    height: 28,
                    padding: '0 10px',
                    border: '1px solid transparent',
                    borderRadius: 4,
                    background: 'transparent',
                    color: '#C4715B',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="file"
        accept={accept}
        onChange={handleFile}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </div>
  );
}
